import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BrandKitCardPreview } from '@/components/brand-kits/BrandKitCardPreview';
import { HorizontalScrollList } from '@/components/HorizontalScrollList';
import { groupTemplatesForCarousel } from '@/lib/brand-kit/sortUserTemplates';
import { cn } from '@/lib/utils';

export interface EditorTemplateKit {
  id: string;
  name: string;
  is_default?: boolean | null;
  is_public?: boolean | null;
  data?: unknown;
  preview_video_url?: string | null;
  preview_poster_url?: string | null;
}

interface EditorTemplatesCarouselProps {
  kits: readonly EditorTemplateKit[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

function TemplateGroupLabel({
  testId,
  children,
}: {
  testId: string;
  children: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex w-6 shrink-0 items-center justify-center self-stretch py-1"
    >
      <span className="rotate-180 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wider text-muted-foreground [writing-mode:vertical-rl]">
        {children}
      </span>
    </div>
  );
}

function TemplateCard({
  kit,
  selectedId,
  onSelect,
  disabled,
  defaultLabel,
}: {
  kit: EditorTemplateKit;
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabled: boolean;
  defaultLabel: string;
}) {
  const isSelected = selectedId === kit.id;
  return (
    <button
      type="button"
      data-testid="editor-template-card"
      data-template-id={kit.id}
      disabled={disabled}
      onClick={() => onSelect(kit.id)}
      className={cn(
        'relative w-20 shrink-0 overflow-hidden rounded-md border-2 bg-black text-left transition-colors',
        isSelected ? 'border-primary' : 'border-border hover:border-muted-foreground',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {isSelected && (
        <span className="absolute right-1 top-1 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      <BrandKitCardPreview
        kitData={kit.data}
        previewVideoUrl={kit.preview_video_url}
        previewPosterUrl={kit.preview_poster_url}
      />
      <span className={cn(
        'block truncate px-1 py-0.5 text-center text-[10px] font-medium text-white',
        isSelected ? 'bg-primary/20' : 'bg-black/60',
      )}>
        {kit.name}
        {kit.is_default ? ` (${defaultLabel})` : ''}
      </span>
    </button>
  );
}

export function EditorTemplatesCarousel({
  kits,
  selectedId,
  onSelect,
  disabled = false,
}: EditorTemplatesCarouselProps) {
  const { t } = useTranslation();
  const { userKits, publicKits } = groupTemplatesForCarousel(kits);
  const defaultLabel = t('projectEditor.default');

  return (
    <HorizontalScrollList>
      {userKits.length > 0 && (
        <TemplateGroupLabel testId="template-carousel-user-label">
          {t('projectEditor.myBrandKits')}
        </TemplateGroupLabel>
      )}
      {userKits.map((kit) => (
        <TemplateCard
          key={kit.id}
          kit={kit}
          selectedId={selectedId}
          onSelect={onSelect}
          disabled={disabled}
          defaultLabel={defaultLabel}
        />
      ))}
      {userKits.length > 0 && publicKits.length > 0 && (
        <div
          data-testid="template-carousel-separator"
          aria-hidden
          className="w-px shrink-0 self-stretch bg-border"
        />
      )}
      {publicKits.length > 0 && (
        <TemplateGroupLabel testId="template-carousel-public-label">
          {t('projectEditor.publicBrandKits')}
        </TemplateGroupLabel>
      )}
      {publicKits.map((kit) => (
        <TemplateCard
          key={kit.id}
          kit={kit}
          selectedId={selectedId}
          onSelect={onSelect}
          disabled={disabled}
          defaultLabel={defaultLabel}
        />
      ))}
    </HorizontalScrollList>
  );
}
